import { captureApiError } from '@/helpers/sentry';
import { checkVerifyAccess } from '@/helpers/verifyToken.ts';
import { i18n } from '@/locales/i18n';
import { InvoiceStatus } from '@/pages/Invoices/Invoices.types';
import router from '@/router';
import { useToastsStore } from '@/stores/toasts';
import { AccentColors, ApiMethod, ErrorCodes, RouteNames } from '@/types/enums';

export interface FetchOptions {
  method: ApiMethod;
  body?: string | FormData;
  headers?: Record<string, string>;
  paymentStatus?: InvoiceStatus;
  noContentType?: boolean;
  noReLoginToast?: boolean;
  noToast?: boolean;
}

export interface ApiResponse<T> {
  data: T | null;
  error: any;
}

const BASE_URL: string = import.meta.env.VITE_APP_API;

export const useFetch = async <T>(url: string, options: FetchOptions): Promise<ApiResponse<T>> => {
  const toastStore = useToastsStore();

  const checkVerifyAccessResponse = (await checkVerifyAccess()) as unknown;

  if (checkVerifyAccessResponse instanceof Error) {
    const error = checkVerifyAccessResponse as Error;

    if (error.message === ErrorCodes.REFRESH_TOKEN_ERROR_NOT_FOUND) {
      const authPathnames = ['/panel/sign-in', '/panel/sign-up', '/panel/forget-password'];
      if (!authPathnames.includes(window.location.pathname)) {
        router.push({ name: RouteNames.SignIn });
      }

      toastStore.show({
        accent: AccentColors.Danger,
        text: i18n.global.t(`errors.${ErrorCodes.REFRESH_TOKEN_ERROR_NOT_FOUND}`),
      });

      return { data: null, error };
    }
  }

  const requestOptions: RequestInit = {
    credentials: 'include',
    method: options.method,
    ...(options.body ? { body: options.body } : {}),
    headers: {
      ...(options.noContentType ? {} : { 'Content-Type': 'application/json' }),
      ...(options.headers || {}),
    },
  };

  try {
    const response = await fetch(`${BASE_URL}/${url}`, requestOptions);

    if (!response.ok) {
      // Request failed
      const error = await response.json();
      // error_message is for auth service
      let errorCode: ErrorCodes = error.code || error.error_message || ErrorCodes.UNKNOWN_ERROR;

      if (error.error) {
        // Handle payment errors, because we need to have access to invoice status to show proper
        // error, we are forced to pass paymentStatus as well to be able to show proper error, this
        // is only for payment invoice requests
        errorCode = ErrorCodes['PAYMENT-UNKNOWN-ERROR'];

        const paymentError: ErrorCodes = error.error;
        if (ErrorCodes[paymentError]) {
          // Check to see if error is a valid error code and exist in the ErrorCodes
          errorCode = error.error;

          if (error.error === ErrorCodes['EXPIRE-INVOICE']) {
            if (options.paymentStatus === InvoiceStatus.INVOICE_STATUS_PENDING) {
              errorCode = ErrorCodes['EXPIRE-INVOICE'];
            } else if (options.paymentStatus === InvoiceStatus.INVOICE_STATUS_APPROVED) {
              errorCode = ErrorCodes['EXPIRE-INVOICE-RENEW'];
            }
          }
        }
      }

      if (errorCode === ErrorCodes.UNAUTHORIZED_ERROR) {
        // Only redirect user to sign-in page if they are not in auth relate pages
        const authPathnames = ['/panel/sign-in', '/panel/sign-up', '/panel/forget-password'];
        if (!authPathnames.includes(window.location.pathname)) {
          router.push({ name: RouteNames.SignIn });
        }

        // Session expired, sign out the user
        if (!options.noReLoginToast) {
          toastStore.show({
            accent: AccentColors.Danger,
            text: i18n.global.t('sign-in.please-sign-in-again'),
          });
        }
      } else if (errorCode === ErrorCodes['USER_MAXIMUM_LIMIT_REACHED'] && error.details) {
        // Users file import maximum limit reached
        // same error is thrown for creating a single user without the  maxUsersLimit and totalUsers fields
        toastStore.show({
          accent: AccentColors.Danger,
          text: i18n.global.t(`errors.USER_MAXIMUM_LIMIT_REACHED_WITH_DETAILS`, {
            limit: error.details.maxUsersLimit,
            total: error.details.totalUsers,
          }),
        });
      } else if (errorCode === ErrorCodes['USER_IMPORT_LIMIT_EXCEEDED']) {
        // Users file import limit exceeded
        toastStore.show({
          accent: AccentColors.Danger,
          text: i18n.global.t(`errors.${ErrorCodes[errorCode]}`, {
            limit: error.details.importLimit,
          }),
        });
      } else if (errorCode === ErrorCodes['USER_INVALID_INFO']) {
        // Either there are invalid info or already exist users
        if (error.details.invalidChannels.length || error.details.invalidUserInfoUsernames.length) {
          toastStore.show({
            accent: AccentColors.Danger,
            text: i18n.global.t(`errors.${ErrorCodes[errorCode]}`),
          });
        }
      } else if (!options.noToast) {
        toastStore.show({
          accent: AccentColors.Danger,
          text: i18n.global.t(`errors.${ErrorCodes[errorCode]}`),
        });
      }

      // Capture API errors to Sentry (excluding auth errors which are expected)
      if (errorCode !== ErrorCodes.UNAUTHORIZED_ERROR) {
        captureApiError(url, errorCode, response.status, error.details);
      }

      return { data: null, error };
    }

    return { data: await response.json(), error: null };
  } catch (error) {
    console.error('Error fetching data:', error);
    // Capture network errors to Sentry
    captureApiError(url, 'NETWORK_ERROR', undefined, { error: String(error) });
    return { data: null, error };
  }
};
